import { useApolloClient } from '@apollo/client';
import { useMemo, useState } from 'react';

import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { MergeRecordsModal } from '@/components/modals/MergeRecordsModal';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useGetEntityOptions } from '@/modules/entities/hooks/useGetEntityOptions';
import { ROUTE_KEYS } from '@/navigation/constants';
import { EntityKind } from '@/types/schema';

import { useMergeEntityMutation } from './MergeEntityModal.generated';
import { isEligibleMergeCandidate } from './MergeEntityModal.utils';

interface MergeEntityModalProps {
  entityId: string;
  entityKind: EntityKind;
  entityName: string;
  householdId: string;
  onClose: () => void;
}

export function MergeEntityModal({
  entityId,
  entityKind,
  entityName,
  householdId,
  onClose,
}: MergeEntityModalProps) {
  const [keepEntityId, setKeepEntityId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState(false);
  const { entityOptions, loading, error } = useGetEntityOptions(householdId);
  const { showFeedback } = useFeedback();
  const { navigate } = useNavigateToRoute();
  const { reportError } = useReportError();
  const client = useApolloClient();

  const candidates = useMemo(
    () =>
      entityOptions.filter((candidate) =>
        isEligibleMergeCandidate(
          { kind: entityKind, value: entityId },
          candidate
        )
      ),
    [entityId, entityKind, entityOptions]
  );
  const [mergeEntity, { loading: isMerging }] = useMergeEntityMutation({
    onError: () => setSubmitError(true),
  });

  const handleClose = (reason: 'confirm' | 'cancel') => {
    if (isMerging) return;
    if (reason === 'cancel') {
      onClose();
      return;
    }
    if (!keepEntityId) return;

    setSubmitError(false);
    void mergeEntity({
      variables: {
        input: { keepEntityID: keepEntityId, removeEntityID: entityId },
      },
    })
      .then((result) => {
        if (!result.data) return;
        const keptEntityId = result.data.mergeEntities.id;
        void navigate(ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS, {
          householdId,
          entityId: keptEntityId,
        });
        onClose();
        showFeedback('Entities merged successfully', { variant: 'success' });

        // The source no longer exists. Refresh related views without waiting on
        // its detail queries or blocking navigation to the retained entity.
        void client
          .refetchQueries({
            updateCache: (cache) => {
              cache.evict({
                id: cache.identify({ __typename: 'Entity', id: keptEntityId }),
              });
              cache.evict({
                id: cache.identify({ __typename: 'Entity', id: entityId }),
              });
              cache.evict({ fieldName: 'entities' });
            },
            onQueryUpdated: (query) =>
              query.variables?.entityId === entityId ? false : query.refetch(),
          })
          .catch((refreshError: Error) => {
            reportError(
              'Failed to refresh data after merging entities',
              refreshError
            );
          });
      })
      .catch(() => undefined);
  };

  return (
    <MergeRecordsModal
      heading="Merge entities"
      subheading={`Merge “${entityName}” with another entity.`}
      duplicateName={entityName}
      pending={isMerging}
      disabled={!keepEntityId}
      confirmText={submitError ? 'Retry merge' : 'Merge and remove duplicate'}
      onClose={() => handleClose('cancel')}
      onConfirm={() => handleClose('confirm')}
      moved="Documents, policies and connected data from the duplicate entity merge into the selected entity"
      discarded="Holdings and integration connections are discarded"
      retainedHeading="Selected entity"
      retained="No changes to its owners, details and holdings"
      destinationInput={
        <TypeaheadSelectInput<string | null>
          label="Merges into"
          placeholder={loading ? 'Loading entities…' : 'Select an entity'}
          hideEmptyOption
          disabled={loading || isMerging}
          options={candidates}
          value={keepEntityId}
          onChange={(_event, value) => setKeepEntityId(value)}
        />
      }
    >
      {error && (
        <Callout severity="error" role="alert">
          We couldn&apos;t load entities. Close this window and try again.
        </Callout>
      )}
      {submitError && (
        <Callout severity="error" role="alert">
          We couldn&apos;t merge these entities. Review the selection and retry.
        </Callout>
      )}
    </MergeRecordsModal>
  );
}
